import { useEffect, useState } from 'react';
import {
  MOBILE_HEADER_ACTIONS,
  type MobileHeaderActionId,
} from './mobileHeaderActions';

// Storage delle preferenze "Barra mobile inferiore". È un set di chiavi
// indipendente da quello dell'header mobile: lo stesso utente può scegliere
// liberamente cosa vedere nei due slot, anche le stesse icone in entrambi.
//
// Lo slot "Altro" della bottom bar non vive qui: è hard-coded nel componente
// `MobileBottomNav` e apre il drawer hamburger esistente.

export const DEFAULT_MOBILE_BOTTOM_NAV_ACTIONS: MobileHeaderActionId[] = [
  'dashboard',
  'messaging-chat',
  'sip-phone',
  'notifications',
];

export const MIN_MOBILE_BOTTOM_NAV_ACTIONS = 3;
export const MAX_MOBILE_BOTTOM_NAV_ACTIONS = 4;

const STORAGE_KEY_PREFIX = 'edgvoip:mobile-bottom-nav:';
const CHANGED_EVENT = 'edgvoip:mobile-bottom-nav-changed';

const VALID_IDS = new Set<string>(MOBILE_HEADER_ACTIONS.map((a) => a.id));

function getStorageKey(userId: string | undefined): string | null {
  if (!userId) return null;
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function sanitize(input: unknown): MobileHeaderActionId[] {
  const result: MobileHeaderActionId[] = [];
  const seen = new Set<string>();
  if (Array.isArray(input)) {
    for (const id of input) {
      if (typeof id === 'string' && VALID_IDS.has(id) && !seen.has(id)) {
        result.push(id as MobileHeaderActionId);
        seen.add(id);
        if (result.length >= MAX_MOBILE_BOTTOM_NAV_ACTIONS) break;
      }
    }
  }
  if (result.length < MIN_MOBILE_BOTTOM_NAV_ACTIONS) {
    for (const id of DEFAULT_MOBILE_BOTTOM_NAV_ACTIONS) {
      if (!seen.has(id)) {
        result.push(id);
        seen.add(id);
        if (result.length >= MIN_MOBILE_BOTTOM_NAV_ACTIONS) break;
      }
    }
  }
  return result;
}

export function getMobileBottomNavActions(
  userId: string | undefined,
): MobileHeaderActionId[] {
  const key = getStorageKey(userId);
  if (!key || typeof window === 'undefined') {
    return [...DEFAULT_MOBILE_BOTTOM_NAV_ACTIONS];
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [...DEFAULT_MOBILE_BOTTOM_NAV_ACTIONS];
    return sanitize(JSON.parse(raw));
  } catch {
    return [...DEFAULT_MOBILE_BOTTOM_NAV_ACTIONS];
  }
}

export function setMobileBottomNavActions(
  userId: string | undefined,
  ids: MobileHeaderActionId[],
): MobileHeaderActionId[] {
  const sanitized = sanitize(ids);
  const key = getStorageKey(userId);
  if (key && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(key, JSON.stringify(sanitized));
      window.dispatchEvent(
        new CustomEvent(CHANGED_EVENT, { detail: { userId, ids: sanitized } }),
      );
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }
  return sanitized;
}

export function useMobileBottomNavActions(
  userId: string | undefined,
): MobileHeaderActionId[] {
  const [ids, setIds] = useState<MobileHeaderActionId[]>(() =>
    getMobileBottomNavActions(userId),
  );

  useEffect(() => {
    setIds(getMobileBottomNavActions(userId));
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !userId || detail.userId === userId) {
        setIds(getMobileBottomNavActions(userId));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === getStorageKey(userId)) {
        setIds(getMobileBottomNavActions(userId));
      }
    };
    window.addEventListener(CHANGED_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  return ids;
}
